import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Briefcase, MapPin, DollarSign, Heart, ExternalLink, ArrowLeft, Building2, Share2, Facebook, Twitter, Linkedin, Home } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';
import Footer from '../components/Footer';

const JobDetailPage = ({ user }) => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(true);
  const [prevJob, setPrevJob] = useState(null);
  const [nextJob, setNextJob] = useState(null);
  const [relatedJobs, setRelatedJobs] = useState([]);

  useEffect(() => {
    fetchJobDetails();
    if (user) {
      checkIfFavorite();
    }
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      const response = await fetch(`${API}/jobs/${jobId}`);
      if (response.ok) {
        const data = await response.json();
        setJob(data);
        await fetchNavigation(data);
      } else {
        toast.error('Oferta no encontrada');
        navigate('/ofertas');
      }
    } catch (error) {
      console.error('Error fetching job:', error);
      toast.error('Error al cargar la oferta');
    } finally {
      setLoading(false);
    }
  };

  const fetchNavigation = async (currentJob) => {
    try {
      const response = await fetch(`${API}/jobs`);
      if (response.ok) {
        const allJobs = await response.json();
        const currentIndex = allJobs.findIndex(j => j.id === currentJob.id);
        
        if (currentIndex > 0) {
          setPrevJob(allJobs[currentIndex - 1]);
        } else {
          setPrevJob(null);
        }
        
        if (currentIndex < allJobs.length - 1) {
          setNextJob(allJobs[currentIndex + 1]);
        } else {
          setNextJob(null);
        }

        // Related jobs (same location or sector)
        const related = allJobs
          .filter(j => j.id !== currentJob.id && (j.location === currentJob.location || j.sector === currentJob.sector))
          .slice(0, 3);
        setRelatedJobs(related);
      }
    } catch (error) {
      console.error('Error fetching navigation:', error);
    }
  };

  const checkIfFavorite = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await fetch(`${API}/jobs/${jobId}/is-favorite`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setIsFavorite(data.is_favorite);
      }
    } catch (error) {
      console.error('Error checking favorite:', error);
    }
  };

  const toggleFavorite = async () => {
    if (!user) {
      toast.error('Inicia sesión para guardar ofertas');
      navigate('/auth');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const method = isFavorite ? 'DELETE' : 'POST';
      const response = await fetch(`${API}/jobs/${jobId}/favorite`, {
        method,
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        setIsFavorite(!isFavorite);
        toast.success(isFavorite ? 'Eliminado de favoritos' : 'Añadido a favoritos');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Error al actualizar favoritos');
    }
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = job?.title || '';

  const handleShare = (platform) => {
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle + ' - JobSuiza')}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareTitle + ' ' + shareUrl)}`
    };
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const getSpanishLevelBadge = (level) => {
    const variants = {
      native: { color: 'bg-red-100 text-red-700', label: 'Nativo' },
      advanced: { color: 'bg-red-100 text-red-600', label: 'Avanzado' },
      intermediate: { color: 'bg-orange-100 text-orange-700', label: 'Intermedio' },
      basic: { color: 'bg-amber-100 text-amber-700', label: 'Básico' },
      'not-required': { color: 'bg-gray-100 text-gray-700', label: 'No requerido' },
    };
    const variant = variants[level] || variants['not-required'];
    return <Badge className={`${variant.color} font-medium text-base px-4 py-1`}>{variant.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-slate-600">Cargando detalles...</p>
        </div>
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <Briefcase className="w-7 h-7 text-red-600" />
              <span className="text-xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent">
                JobSuiza
              </span>
            </Link>

            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => navigate('/')} data-testid="back-home-btn" className="hidden sm:flex">
                <Home className="w-4 h-4 mr-2" />
                Inicio
              </Button>
              <Button variant="ghost" onClick={() => navigate('/ofertas')} data-testid="back-offers-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Ofertas
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Job Details */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="shadow-2xl">
          <CardContent className="p-8 md:p-12">
            {/* Header */}
            <div className="flex justify-between items-start mb-8">
              <div className="flex-1">
                {job.is_featured && (
                  <Badge className="bg-amber-100 text-amber-700 mb-3">⭐ Oferta Destacada</Badge>
                )}
                <h1 className="text-4xl font-bold mb-4 text-slate-900">{job.title}</h1>
                <div className="flex items-center space-x-2 mb-4">
                  <Building2 className="w-6 h-6 text-red-600" />
                  <p className="text-2xl font-semibold text-red-600">{job.company}</p>
                </div>

                <div className="flex flex-wrap gap-4 mb-6">
                  <div className="flex items-center text-slate-700">
                    <MapPin className="w-5 h-5 mr-2" />
                    <span className="text-lg">{job.location}</span>
                  </div>
                  {job.salary && (
                    <div className="flex items-center text-slate-700">
                      <DollarSign className="w-5 h-5 mr-2" />
                      <span className="text-lg">{job.salary}</span>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-3">
                  <Badge variant="outline" className="text-base px-3 py-1">{job.contract_type || 'Tiempo completo'}</Badge>
                  <Badge variant="outline" className="text-base px-3 py-1">{job.sector || 'General'}</Badge>
                  {getSpanishLevelBadge(job.spanish_level)}
                </div>
              </div>

              <Button
                size="lg"
                variant={isFavorite ? 'default' : 'outline'}
                onClick={toggleFavorite}
                className="ml-4"
              >
                <Heart className={`w-5 h-5 mr-2 ${isFavorite ? 'fill-current' : ''}`} />
                {isFavorite ? 'Guardado' : 'Guardar'}
              </Button>
            </div>

            {/* Description */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-slate-900">Descripción</h2>
              <p className="text-lg text-slate-700 leading-relaxed whitespace-pre-line">{job.description}</p>
            </div>

            {/* Requirements */}
            {job.requirements && job.requirements.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold mb-4 text-slate-900">Requisitos</h2>
                <ul className="space-y-3">
                  {job.requirements.map((req, index) => (
                    <li key={index} className="flex items-start">
                      <span className="inline-block w-2 h-2 bg-red-600 rounded-full mt-2 mr-3"></span>
                      <span className="text-lg text-slate-700">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Spanish Level Info */}
            <div className="mb-8 p-6 bg-red-50 rounded-2xl">
              <h3 className="text-xl font-bold mb-2 text-slate-900">Nivel de Español Requerido</h3>
              <div className="flex items-center space-x-3">
                <span className="text-lg text-slate-700">Esta posición requiere:</span>
                {getSpanishLevelBadge(job.spanish_level)}
              </div>
            </div>

            {/* Apply Button */}
            <div className="flex gap-4 mb-8">
              <Button
                size="lg"
                className="flex-1 h-14 text-lg bg-red-600 hover:bg-red-700"
                onClick={() => window.open(job.url, '_blank')}
              >
                <ExternalLink className="w-5 h-5 mr-2" />
                Aplicar en el sitio original
              </Button>
            </div>

            {/* Share Buttons */}
            <div className="mb-8 pt-6 border-t border-slate-200">
              <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center">
                <Share2 className="w-4 h-4 mr-2" />
                Compartir oferta
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => handleShare('facebook')}
                  className="flex items-center justify-center w-10 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleShare('twitter')}
                  className="flex items-center justify-center w-10 h-10 bg-sky-500 hover:bg-sky-600 text-white rounded-full transition-colors"
                >
                  <Twitter className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleShare('linkedin')}
                  className="flex items-center justify-center w-10 h-10 bg-blue-700 hover:bg-blue-800 text-white rounded-full transition-colors"
                >
                  <Linkedin className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleShare('whatsapp')}
                  className="flex items-center justify-center w-10 h-10 bg-green-500 hover:bg-green-600 text-white rounded-full transition-colors"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Navigation (Previous/Next) */}
            <div className="pt-6 border-t border-slate-200">
              <div className="flex justify-between items-center gap-4">
                {prevJob ? (
                  <Link to={`/ofertas/${prevJob.id}`} className="flex-1 group">
                    <div className="p-4 rounded-xl border border-slate-200 hover:border-red-300 hover:bg-red-50 transition-all">
                      <p className="text-sm text-slate-500 mb-1">← Oferta Anterior</p>
                      <p className="font-semibold text-slate-800 group-hover:text-red-600 transition-colors line-clamp-2">
                        {prevJob.title}
                      </p>
                      <p className="text-sm text-slate-500">{prevJob.company}</p>
                    </div>
                  </Link>
                ) : (
                  <div className="flex-1" />
                )}
                
                {nextJob ? (
                  <Link to={`/ofertas/${nextJob.id}`} className="flex-1 group text-right">
                    <div className="p-4 rounded-xl border border-slate-200 hover:border-red-300 hover:bg-red-50 transition-all">
                      <p className="text-sm text-slate-500 mb-1">Oferta Siguiente →</p>
                      <p className="font-semibold text-slate-800 group-hover:text-red-600 transition-colors line-clamp-2">
                        {nextJob.title}
                      </p>
                      <p className="text-sm text-slate-500">{nextJob.company}</p>
                    </div>
                  </Link>
                ) : (
                  <div className="flex-1" />
                )}
              </div>
            </div>

            {/* Related Jobs */}
            {relatedJobs.length > 0 && (
              <div className="mt-8 pt-6 border-t border-slate-200">
                <h3 className="text-2xl font-bold mb-6 text-slate-900">Ofertas Similares</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {relatedJobs.map((relatedJob) => (
                    <Link key={relatedJob.id} to={`/ofertas/${relatedJob.id}`} className="group">
                      <div className="p-4 rounded-xl border border-slate-200 hover:border-red-300 hover:bg-red-50 transition-all h-full">
                        <h4 className="font-semibold text-slate-800 group-hover:text-red-600 transition-colors mb-2 line-clamp-2">
                          {relatedJob.title}
                        </h4>
                        <p className="text-sm text-red-600 mb-1">{relatedJob.company}</p>
                        <p className="text-sm text-slate-500">{relatedJob.location}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default JobDetailPage;
